import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { AppService } from '../../../app.service';
import { isSsoOnPrivilegedList } from '../../shared/admin-privilege.util';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'gtw-bizz-hier',
    templateUrl: './bizz-hier.component.html',
    styleUrls: ['./bizz-hier.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BizzHierComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  lastSyncedDate = '';
  searchText = '';
  syncing = false;
  treeLoading = false;
  isPrivilegedSSO = false;
  leftApp = '';
  leftHierarchyId: number | null = null;
  rightApp = '';
  rightHierarchyId: number | null = null;
  appsLeft: any[] = [];
  appsRight: any[] = [];
  hierarchiesLeft: any[] = [];
  hierarchiesRight: any[] = [];
  leftAlert = 'Please Select the Application and Hierarchy';
  rightAlert = 'Please Select the Application and Hierarchy';
  leftNodes: any[] = [];
  rightNodes: any[] = [];
  treeCompared = false;
  onlyInGoldCount = 0;
  onlyInTaxCount = 0;
  editedCount = 0;
  movedCount = 0;
  showAssignHierarchyModal = false;
  showCreateHierarchyModal = false;
  showBusinessUnitModal = false;
  showCreateHierarchyExcelModal = false;
  assignLoading = false;
  createLoading = false;
  buLoading = false;
  excelLoading = false;
  assignTaxYear = '';
  assignScenario: number | null = null;
  assignJcdKey: number | null = null;
  assignNewHierarchyId: number | null = null;
  assignJcds: any[] = [];
  assignScenarios: any[] = [];
  assignHierarchies: any[] = [];
  assignDefaultHierarchyName = 'None';
  createHierarchyName = '';
  createSrcHierarchyId: number | null = null;
  createDestAppCode = '';
  createDestJcdKey: number | null = null;
  createDestHierarchies: any[] = [];
  excelDestAppCode = '';
  excelDestJcdKey: number | null = null;
  excelHierarchyName = '';
  excelIncludeHeader = true;
  excelSelectedFile: File | null = null;
  excelSelectedFileName = '';
  excelDestHierarchies: any[] = [];
  buCode = '';
  buDesc = '';
  buLineOfBusiness = '';
  buDetailBsla = '';
  buEstDataCollection = '';
  buParentNode: any = null;
  buEditMode = false;
  buEditNode: any = null;
  movingNode: any = null;
  dragMovingNode: any = null;
  dragOverNodeKey: any = null;
  dragOverRoot = false;
  taxYearOptions: { text: string; value: string }[] = [];

  private adminApiUrl = environment.admin_api_url;
  private baseUrl = environment.base_url;

  constructor(
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.initPrivilegeCheck();
    this.buildTaxYearOptions();
    this.loadApps();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initPrivilegeCheck(): void {
    const serverConfig = this.appService.SERVER_CONFIG;
    const privileged = serverConfig?.configSettings?.privileged_sso_list;
    const sso = this.appService.USER_SETTINGS?.user?.sso_id ?? '';
    this.isPrivilegedSSO = isSsoOnPrivilegedList(sso, privileged);
  }

  private buildTaxYearOptions(): void {
    const raw = this.appService.TAX_YEARS;
    if (!raw) {
      this.taxYearOptions = [];
      return;
    }
    let items: any[] = [];
    if (Array.isArray(raw)) {
      items = raw;
    } else if (typeof raw === 'object') {
      items = ([] as any[]).concat(...Object.values(raw));
    }
    const seen = new Set<string>();
    this.taxYearOptions = items
      .filter((item: any) => {
        const v = String(item?.value ?? item?.label ?? item ?? '');
        if (!v || seen.has(v)) return false;
        seen.add(v);
        return true;
      })
      .map((item: any) => ({
        text: String(item?.label ?? item?.value ?? item ?? ''),
        value: String(item?.value ?? item?.label ?? item ?? ''),
      }));
  }

  private loadApps(): void {
    const raw = this.appService.USER_MENU_V1 ?? this.appService.USER_MENU ?? [];
    const menuItems: any[] = Array.isArray(raw) ? raw : [];
    const apps = menuItems
      .filter((item: any) => item != null)
      .map((item: any) => ({
        id: String(item.id ?? item.channel_key ?? item.child_id ?? ''),
        label: String(item.label ?? item.text ?? item.channel_name ?? ''),
        jcd_key: String(item.jcd_key ?? 0),
        channel_key: String(item.channel_key ?? item.id ?? ''),
      }))
      .filter((a: any) => a.id);

    const hasGold = apps.some((a: any) => a.label.toUpperCase() === 'GOLD' || a.id === '0');
    if (!hasGold) {
      apps.push({ id: '0', label: 'GOLD', jcd_key: '0', channel_key: '0' });
    }

    this.appsLeft = apps;
    this.appsRight = apps.filter((a: any) => a.label.toUpperCase() !== 'GOLD');
    this.cdr.detectChanges();
  }

  onAppChange(side: 'left' | 'right'): void {
    this.loadHierarchies(side);
  }

  onHierarchyChange(side: 'left' | 'right'): void {
    this.loadTree(side);
  }

  compareHierarchies(): void {
    const left = this.flattenByCode(this.leftNodes);
    const right = this.flattenByCode(this.rightNodes);
    const leftCodes = new Set(Object.keys(left));
    const rightCodes = new Set(Object.keys(right));

    this.onlyInGoldCount = [...leftCodes].filter((c) => !rightCodes.has(c)).length;
    this.onlyInTaxCount = [...rightCodes].filter((c) => !leftCodes.has(c)).length;
    this.editedCount = [...rightCodes].filter((c) => left[c] && String(left[c].meName || '') !== String(right[c].meName || '')).length;
    // Keeping moved count as conservative 0 here; full parent-path move parity still resides in AngularJS flow.
    this.movedCount = 0;
    this.treeCompared = true;
    this.cdr.detectChanges();
  }

  isNodeVisible(node: any): boolean {
    const term = String(this.searchText || '').trim().toLowerCase();
    if (!term) return true;
    const name = String(node?.meName || '').toLowerCase();
    const code = String(node?.meCode || '').toLowerCase();
    if (name.includes(term) || code.includes(term)) return true;
    if (Array.isArray(node?.children)) {
      return node.children.some((child: any) => this.isNodeVisible(child));
    }
    return false;
  }

  toggleNode(node: any): void {
    if (node?.children?.length) {
      node._collapsed = !node._collapsed;
      this.cdr.detectChanges();
    }
  }

  collapseAll(side: 'left' | 'right'): void {
    const nodes = side === 'left' ? this.leftNodes : this.rightNodes;
    const setCollapsed = (arr: any[], collapsed: boolean) => {
      for (const n of arr || []) {
        if (n?.children?.length) {
          n._collapsed = collapsed;
          setCollapsed(n.children, collapsed);
        }
      }
    };
    const allCollapsed = this.areAllCollapsed(nodes);
    setCollapsed(nodes, !allCollapsed);
    this.cdr.detectChanges();
  }

  private areAllCollapsed(nodes: any[]): boolean {
    for (const n of nodes || []) {
      if (n?.children?.length && !n._collapsed) return false;
      if (n?.children?.length && !this.areAllCollapsed(n.children)) return false;
    }
    return true;
  }

  assignModal(): void {
    this.showAssignHierarchyModal = true;
    this.assignTaxYear = this.currentTaxYear;
    this.loadAssignScenariosAndJcd();
  }

  openHierarchyModel(): void {
    this.showCreateHierarchyModal = true;
    this.createHierarchyName = '';
    this.createSrcHierarchyId = this.leftHierarchyId;
    this.createDestAppCode = this.rightApp;
    this.createDestJcdKey = this.findAppById(this.appsRight, this.createDestAppCode)?.jcd_key ?? null;
    this.buCode = '';
    this.buDesc = '';
    this.buLineOfBusiness = 'IND';
    this.buDetailBsla = 'N';
    this.loadCreateDestinationHierarchies();
  }

  openHierarchyExcelModel(): void {
    this.showCreateHierarchyExcelModal = true;
    this.excelDestAppCode = this.rightApp;
    this.excelDestJcdKey = this.findAppById(this.appsRight, this.excelDestAppCode)?.jcd_key ?? null;
    this.excelHierarchyName = '';
    this.excelIncludeHeader = true;
    this.excelSelectedFile = null;
    this.excelSelectedFileName = '';
    this.loadExcelDestinationHierarchies();
  }

  openBusinessUnitModal(): void {
    if (!this.rightHierarchyId || !this.rightApp) {
      this.gtwSnackbarService.openSnackBar('Please select destination application and hierarchy first', 'gtw-snackbar--yellow');
      return;
    }
    this.buEditMode = false;
    this.buEditNode = null;
    this.buParentNode = null;
    this.showBusinessUnitModal = true;
    this.buCode = '';
    this.buDesc = '';
    this.buLineOfBusiness = '';
    this.buDetailBsla = '';
    this.buEstDataCollection = '';
  }

  openAddChildBusinessUnitModal(node: any): void {
    if (!this.rightHierarchyId || !this.rightApp) {
      this.gtwSnackbarService.openSnackBar('Please select destination application and hierarchy first', 'gtw-snackbar--yellow');
      return;
    }
    this.buEditMode = false;
    this.buEditNode = null;
    this.buParentNode = node;
    this.showBusinessUnitModal = true;
    this.buCode = '';
    this.buDesc = '';
    this.buLineOfBusiness = '';
    this.buDetailBsla = '';
    this.buEstDataCollection = '';
  }

  openEditBusinessUnitModal(node: any): void {
    this.buEditMode = true;
    this.buEditNode = node;
    this.buParentNode = null;
    this.showBusinessUnitModal = true;
    this.buCode = String(node?.meCode ?? '');
    this.buDesc = String(node?.meName ?? '');
    this.buLineOfBusiness = String(node?.line_of_business ?? '');
    this.buDetailBsla = String(node?.detail_bsla_ind ?? '');
    this.buEstDataCollection = String(node?.EST_Data_coll_level ?? node?.estDataCollLvl ?? '');
  }

  closeAssignModal(): void {
    this.showAssignHierarchyModal = false;
  }

  closeCreateHierarchyModal(): void {
    this.showCreateHierarchyModal = false;
  }

  closeBusinessUnitModal(): void {
    this.showBusinessUnitModal = false;
    this.buEditMode = false;
    this.buEditNode = null;
    this.buParentNode = null;
  }

  startMoveNode(node: any): void {
    this.movingNode = node;
    this.cdr.detectChanges();
  }

  cancelMoveNode(): void {
    this.movingNode = null;
    this.cdr.detectChanges();
  }

  canDropOnNode(node: any): boolean {
    if (!this.movingNode || !node) return false;
    if (String(node?.meKey) === String(this.movingNode?.meKey)) return false;
    // Disallow dropping on descendants of the selected node
    return !this.containsNodeByMeKey(this.movingNode, node?.meKey);
  }

  dropMoveNode(node: any): void {
    if (!this.movingNode || !this.canDropOnNode(node) || !this.rightHierarchyId || !this.rightApp) {
      return;
    }
    this.executeMoveHierarchy(this.movingNode, node);
    this.movingNode = null;
  }

  dropMoveNodeToRoot(): void {
    if (!this.movingNode) return;
    this.executeMoveHierarchy(this.movingNode, null);
    this.movingNode = null;
  }

  onDragStartNode(event: DragEvent, node: any): void {
    if (!node) return;
    this.dragMovingNode = node;
    this.dragOverNodeKey = null;
    this.dragOverRoot = false;
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(node?.meKey ?? ''));
      }
    } catch (e) {
      // noop
    }
    this.cdr.detectChanges();
  }

  onDragOverNode(event: DragEvent, node: any): void {
    if (!this.dragMovingNode || !node) return;
    if (!this.canDragDropOnNode(node)) return;
    event.preventDefault();
    this.dragOverNodeKey = node?.meKey;
  }

  onDragOverRoot(event: DragEvent): void {
    if (!this.dragMovingNode) return;
    event.preventDefault();
    this.dragOverNodeKey = null;
    this.dragOverRoot = true;
  }

  onDropByGesture(event: DragEvent, node: any): void {
    event.preventDefault();
    if (!this.dragMovingNode || !node || !this.canDragDropOnNode(node)) {
      this.dragOverNodeKey = null;
      this.dragOverRoot = false;
      this.cdr.detectChanges();
      return;
    }
    const srcNode = this.dragMovingNode;
    this.dragOverNodeKey = null;
    this.dragOverRoot = false;
    this.executeMoveHierarchy(srcNode, node);
  }

  onDropRootByGesture(event: DragEvent): void {
    event.preventDefault();
    if (!this.dragMovingNode) {
      this.dragOverRoot = false;
      this.cdr.detectChanges();
      return;
    }
    const srcNode = this.dragMovingNode;
    this.dragOverRoot = false;
    this.executeMoveHierarchy(srcNode, null);
  }

  onDragEndNode(): void {
    this.dragMovingNode = null;
    this.dragOverNodeKey = null;
    this.dragOverRoot = false;
    this.cdr.detectChanges();
  }

  closeCreateHierarchyExcelModal(): void {
    this.showCreateHierarchyExcelModal = false;
  }

  onAssignTaxYearChange(): void {
    this.assignScenario = null;
    this.assignJcdKey = null;
    this.assignHierarchies = [];
    this.assignNewHierarchyId = null;
    this.assignDefaultHierarchyName = 'None';
    this.loadAssignScenariosAndJcd();
  }

  onAssignApplicationChange(): void {
    this.assignScenario = this.toNumberOrNull(this.assignScenario);
    this.assignJcdKey = this.toNumberOrNull(this.assignJcdKey);
    this.loadAssignHierarchiesAndDefault();
  }

  submitAssignHierarchy(): void {
    if (!this.assignNewHierarchyId) {
      this.gtwSnackbarService.openSnackBar('Please select a hierarchy to assign', 'gtw-snackbar--yellow');
      return;
    }
    this.assignLoading = true;
    const url = `${this.adminApiUrl}/hierarchy/assign?hierarchyId=${this.assignNewHierarchyId}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.assignLoading = false;
        if (res?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Hierarchy assigned successfully', 'gtw-snackbar--green');
          this.showAssignHierarchyModal = false;
        } else {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Hierarchy assign failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.assignLoading = false;
        this.gtwSnackbarService.openSnackBar('Hierarchy assign failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  onCreateDestinationAppChange(): void {
    this.createDestJcdKey = this.findAppById(this.appsRight, this.createDestAppCode)?.jcd_key ?? null;
    this.loadCreateDestinationHierarchies();
  }

  onExcelDestinationAppChange(): void {
    this.excelDestJcdKey = this.findAppById(this.appsRight, this.excelDestAppCode)?.jcd_key ?? null;
    this.loadExcelDestinationHierarchies();
  }

  onExcelFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.length ? input.files[0] : null;
    this.excelSelectedFile = file;
    this.excelSelectedFileName = file?.name ?? '';
    this.cdr.detectChanges();
  }

  submitCreateHierarchy(): void {
    if (!this.createDestAppCode || !this.createDestJcdKey || !this.createHierarchyName) {
      this.gtwSnackbarService.openSnackBar('Please fill required fields', 'gtw-snackbar--yellow');
      return;
    }
    this.createLoading = true;
    const srcHierarchyId = this.createSrcHierarchyId ?? '';
    const taxYear = this.currentTaxYear;
    const scenario = this.currentScenario;
    const name = encodeURIComponent(this.createHierarchyName);
    const url =
      `${this.adminApiUrl}/createHierarchy?srcHierarchyId=${srcHierarchyId}` +
      `&newHierarchyName=${name}&appCode=${this.createDestAppCode}` +
      `&taxYear=${taxYear}&scenarioId=${scenario}&jcdKey=${this.createDestJcdKey}`;
    this.http.post<any>(url, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.createLoading = false;
        if (String(res?.p_retval) === '1') {
          this.gtwSnackbarService.openSnackBar('Hierarchy created successfully', 'gtw-snackbar--green');
          this.showCreateHierarchyModal = false;
          this.rightApp = this.createDestAppCode;
          this.loadHierarchies('right');
        } else {
          this.gtwSnackbarService.openSnackBar('Hierarchy creation failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.createLoading = false;
        this.gtwSnackbarService.openSnackBar('Hierarchy creation failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  submitCreateHierarchyWithExcel(): void {
    if (!this.excelDestAppCode || !this.excelDestJcdKey || !this.excelHierarchyName || !this.excelSelectedFile) {
      this.gtwSnackbarService.openSnackBar('Please fill required fields', 'gtw-snackbar--yellow');
      return;
    }
    if (this.excelHierarchyNameExists) {
      this.gtwSnackbarService.openSnackBar('Hierarchy name already exists in Destination Application', 'gtw-snackbar--yellow');
      return;
    }
    this.excelLoading = true;

    const uploadApiBase = this.baseUrl;
    const uploadUrl = `${uploadApiBase}/uploadTemplate?flowFilename=${encodeURIComponent(this.excelSelectedFile.name)}`;
    const fd = new FormData();
    fd.append('file', this.excelSelectedFile);

    this.http.post<any>(uploadUrl, fd, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (uploadResRaw) => {
        const uploadRes = uploadResRaw?.data ?? uploadResRaw;
        if (String(uploadRes?.callSuccess ?? '') !== '1' || !uploadRes?.file) {
          this.excelLoading = false;
          this.gtwSnackbarService.openSnackBar(
            `Error during uploading the file${uploadRes?.message ? `: ${uploadRes.message}` : ''}`,
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
          return;
        }

        const encodedPath = String(uploadRes.file).replace(/\\/g, '%5C');
        const skipHeader = this.excelIncludeHeader ? 1 : 0;
        const url =
          `${this.adminApiUrl}/uploadExcelBizzHierarchy?fileName=${encodedPath}` +
          `&taxYear=${this.currentTaxYear}&scenarioId=${this.currentScenario}` +
          `&jcdKey=${this.excelDestJcdKey}&hierarchyName=${encodeURIComponent(this.excelHierarchyName)}` +
          `&channelKey=${this.excelDestAppCode}&skipHeader=${skipHeader}`;

        this.http.post<any>(url, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
          next: (resRaw) => {
            const res = resRaw?.data ?? resRaw;
            this.excelLoading = false;
            if (String(res?.callSuccess ?? '') === '1') {
              this.gtwSnackbarService.openSnackBar('Hierarchy created successfully', 'gtw-snackbar--green');
              this.showCreateHierarchyExcelModal = false;
              this.rightApp = this.excelDestAppCode;
              this.loadHierarchies('right');
            } else {
              this.gtwSnackbarService.openSnackBar(
                `Error occurred during inserting data into DB${res?.errorMessage ? `: ${res.errorMessage}` : ''}`,
                'gtw-snackbar--red'
              );
            }
            this.cdr.detectChanges();
          },
          error: () => {
            this.excelLoading = false;
            this.gtwSnackbarService.openSnackBar('Hierarchy creation from excel failed', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          },
        });
      },
      error: () => {
        this.excelLoading = false;
        this.gtwSnackbarService.openSnackBar('Error during uploading the file', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  submitCreateBusinessUnit(): void {
    if (!this.rightHierarchyId || !this.rightApp || !this.buCode || !this.buDesc) {
      this.gtwSnackbarService.openSnackBar('Please fill required fields', 'gtw-snackbar--yellow');
      return;
    }
    const app = this.findAppById(this.appsRight, this.rightApp);
    if (!app?.jcd_key) {
      this.gtwSnackbarService.openSnackBar('Unable to resolve destination application', 'gtw-snackbar--red');
      return;
    }
    this.buLoading = true;
    if (this.buEditMode && this.buEditNode) {
      const payload = {
        meKey: this.buEditNode.meKey,
        groupObjKey: this.buEditNode.groupObjKey,
        description: this.buDesc,
        bUCode: this.buCode,
        line_of_business: this.buLineOfBusiness,
        detail_bsla_ind: this.buDetailBsla,
        srcHierarchyId: this.buEditNode.hierarchyId,
        hierarchyId: this.buEditNode.hierarchyId,
        taxYear: this.currentTaxYear,
        estDataCollLvl: this.buEstDataCollection,
        jcdKey: app.jcd_key,
        scenarioId: this.currentScenario,
      };
      this.http.post<any>(`${this.adminApiUrl}/editBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.buLoading = false;
          if (String(res?.result?.value ?? res?.value ?? '') === '1') {
            this.gtwSnackbarService.openSnackBar('Saved Business Unit Successfully', 'gtw-snackbar--green');
            this.showBusinessUnitModal = false;
            this.loadTree('right');
          } else {
            this.gtwSnackbarService.openSnackBar('Something went wrong, please try again', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.buLoading = false;
          this.gtwSnackbarService.openSnackBar('Business Unit update failed', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
      return;
    }

    const payload = {
      meCode: this.buCode,
      meGoldName: 'None',
      meNonGoldName: this.buDesc,
      refreshIndicator: 'N',
      srcSystemFlag: 'TAX',
      scenarioId: this.currentScenario,
      meParentKey: this.buParentNode?.meKey ?? '',
      status: 'A',
      taxYear: this.currentTaxYear,
      jcdKey: app.jcd_key,
      hierarchyId: this.rightHierarchyId,
      line_of_business: this.buLineOfBusiness,
      detail_bsla_ind: this.buDetailBsla,
      estDataCollLvl: this.buEstDataCollection,
    };
    this.http.post<any>(`${this.adminApiUrl}/addBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.buLoading = false;
        if (res?.p_me_key || res?.callSuccess === '1' || res?.value === '1') {
          this.gtwSnackbarService.openSnackBar('Created Business Unit Successfully', 'gtw-snackbar--green');
          this.showBusinessUnitModal = false;
          this.loadTree('right');
        } else {
          this.gtwSnackbarService.openSnackBar('Business Unit creation failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.buLoading = false;
        this.gtwSnackbarService.openSnackBar('Business Unit creation failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  deleteBusinessUnit(node: any): void {
    if (!this.rightHierarchyId || !this.rightApp || !node) return;
    const app = this.findAppById(this.appsRight, this.rightApp);
    if (!app?.jcd_key) return;
    const payload = {
      meKey: node.meKey,
      meParentKey: node.meParentKey || '',
      groupObjKey: node.groupObjKey,
      hierarchyId: node.hierarchyId,
      taxYear: this.currentTaxYear,
      jcdKey: app.jcd_key,
      scenarioId: this.currentScenario,
    };
    this.treeLoading = true;
    this.http.post<any>(`${this.adminApiUrl}/deleteBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.treeLoading = false;
        if (String(res?.value ?? '') === '1') {
          this.gtwSnackbarService.openSnackBar('Deleted Business Unit Successfully', 'gtw-snackbar--green');
          this.loadTree('right');
        } else if (String(res?.value ?? '') === '2') {
          this.gtwSnackbarService.openSnackBar('Cannot delete business as it is used in code combination.', 'gtw-snackbar--yellow');
        } else {
          this.gtwSnackbarService.openSnackBar('Business Unit deletion failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.treeLoading = false;
        this.gtwSnackbarService.openSnackBar('Business Unit deletion failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  moveBusinessUnitToRoot(node: any): void {
    if (!node) return;
    this.executeMoveHierarchy(node, null);
  }

  autoPushNode(node: any): void {
    if (!node || !this.leftHierarchyId || !this.rightHierarchyId || !this.rightApp) {
      this.gtwSnackbarService.openSnackBar('Please select both left and right hierarchies first', 'gtw-snackbar--yellow');
      return;
    }
    const app = this.findAppById(this.appsRight, this.rightApp);
    if (!app?.jcd_key) return;

    if (this.findNodeInTreeByMeCode(this.rightNodes, node.meCode)) {
      this.gtwSnackbarService.openSnackBar('This BU already exists in destination hierarchy', 'gtw-snackbar--yellow');
      return;
    }

    let parentKey = '';
    if (node.meParentKey) {
      const leftParent = this.findNodeInTreeByMeKey(this.leftNodes, node.meParentKey);
      const rightParent = leftParent ? this.findNodeInTreeByMeCode(this.rightNodes, leftParent.meCode) : null;
      if (!rightParent) {
        this.gtwSnackbarService.openSnackBar('This BU does not have a parent in destination hierarchy. Please add its parent first.', 'gtw-snackbar--yellow');
        return;
      }
      parentKey = String(rightParent.meKey ?? '');
    }

    const payload = {
      meKey: node.meKey,
      meParentKey: parentKey,
      srcHierarchyId: this.leftHierarchyId,
      descHierarchyId: this.rightHierarchyId,
      tax_year: this.currentTaxYear,
      scenario: this.currentScenario,
      jcd_key: app.jcd_key,
    };
    this.treeLoading = true;
    this.http.post<any>(`${this.adminApiUrl}/moveBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.treeLoading = false;
        this.gtwSnackbarService.openSnackBar('Moved Business Unit Successfully', 'gtw-snackbar--green');
        this.loadTree('right');
        this.cdr.detectChanges();
      },
      error: () => {
        this.treeLoading = false;
        this.gtwSnackbarService.openSnackBar('Auto push failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  sync(): void {
    if (!this.isPrivilegedSSO || this.syncing) return;
    this.syncing = true;
    this.treeLoading = true;
    this.cdr.detectChanges();
    const goldJcd = (this.appsLeft || []).find((a: any) => (a.label || a.app_code || '').toString().toLowerCase() === 'gold');
    const jcdKey = goldJcd?.jcd_key ?? 0;
    const taxYear = this.currentTaxYear;
    const scenario = this.currentScenario;
    const url = `${this.adminApiUrl}/syncGoldHierarchy?taxYear=${taxYear}&scenario=${scenario}&jcd=${jcdKey}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.syncing = false;
        this.treeLoading = false;
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Gold synced successfully', 'gtw-snackbar--green');
          this.loadHierarchies('left');
          if (this.rightApp) {
            this.loadHierarchies('right');
          }
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Sync failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.syncing = false;
        this.treeLoading = false;
        this.gtwSnackbarService.openSnackBar('Sync failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  private loadHierarchies(side: 'left' | 'right'): void {
    const appCode = side === 'left' ? this.leftApp : this.rightApp;
    const appList = side === 'left' ? this.appsLeft : this.appsRight;
    const appSelected = appList.find((a: any) => String(a.id) === String(appCode));
    if (!appSelected) {
      if (side === 'left') {
        this.hierarchiesLeft = [];
        this.leftHierarchyId = null;
        this.leftNodes = [];
      } else {
        this.hierarchiesRight = [];
        this.rightHierarchyId = null;
        this.rightNodes = [];
      }
      this.cdr.detectChanges();
      return;
    }

    this.treeLoading = true;
    const taxYear = this.currentTaxYear;
    const scenario = this.currentScenario;
    const jcdKey = appSelected.jcd_key ?? 0;
    const url = `${this.adminApiUrl}/getHierarchiesForApp?taxYear=${taxYear}&scenarioId=${scenario}&jcdKey=${jcdKey}&appCode=${appSelected.id}`;
    this.http.post<any>(url, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : [];
        if (side === 'left') {
          this.hierarchiesLeft = list;
          this.leftHierarchyId = list.length === 1 ? list[0].hierarchy_id : null;
          this.leftNodes = [];
          this.leftAlert = list.length ? this.leftAlert : 'No hierarchies available';
          if (this.leftHierarchyId) this.loadTree('left');
        } else {
          this.hierarchiesRight = list;
          this.rightHierarchyId = list.length === 1 ? list[0].hierarchy_id : null;
          this.rightNodes = [];
          this.rightAlert = list.length ? this.rightAlert : 'No hierarchies available';
          if (this.rightHierarchyId) this.loadTree('right');
        }
        this.treeLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.treeLoading = false;
        if (side === 'left') this.leftAlert = 'Error loading hierarchies';
        else this.rightAlert = 'Error loading hierarchies';
        this.cdr.detectChanges();
      },
    });
  }

  private loadTree(side: 'left' | 'right'): void {
    const hierarchyId = side === 'left' ? this.leftHierarchyId : this.rightHierarchyId;
    const appCode = side === 'left' ? this.leftApp : this.rightApp;
    const appList = side === 'left' ? this.appsLeft : this.appsRight;
    const appSelected = appList.find((a: any) => String(a.id) === String(appCode));
    if (!hierarchyId || !appSelected) {
      if (side === 'left') this.leftAlert = 'Please Select the Application and Hierarchy';
      else this.rightAlert = 'Please Select the Application and Hierarchy';
      this.cdr.detectChanges();
      return;
    }

    this.treeLoading = true;
    const taxYear = this.currentTaxYear;
    const scenario = this.currentScenario;
    const jcdKey = appSelected.jcd_key ?? 0;
    const url = `${this.adminApiUrl}/getBusinessHeirarchyTree?hierarchyId=${hierarchyId}&taxYear=${taxYear}&scenario=${scenario}&jcdKey=${jcdKey}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const nodes = Array.isArray(res) ? res : [];
        if (side === 'left') {
          this.leftNodes = nodes;
          this.leftAlert = nodes.length ? '' : 'There is no data available';
          if (nodes.length && nodes[0]?.dateTime) this.lastSyncedDate = nodes[0].dateTime;
        } else {
          this.rightNodes = nodes;
          this.rightAlert = nodes.length ? '' : 'There is no data available';
        }
        this.treeLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.treeLoading = false;
        if (side === 'left') this.leftAlert = 'Error loading tree';
        else this.rightAlert = 'Error loading tree';
        this.cdr.detectChanges();
      },
    });
  }

  private flattenByCode(nodes: any[]): Record<string, any> {
    const out: Record<string, any> = {};
    const walk = (arr: any[]) => {
      for (const n of arr || []) {
        const code = String(n?.meCode || '');
        if (code) out[code] = n;
        if (Array.isArray(n?.children) && n.children.length) walk(n.children);
      }
    };
    walk(nodes || []);
    return out;
  }

  private get currentTaxYear(): string {
    return String(this.globalService.global?.globalParams?.tax_year ?? '');
  }

  private get currentScenario(): string {
    return String(this.globalService.global?.globalParams?.scenario ?? '');
  }

  private loadAssignScenariosAndJcd(): void {
    if (!this.assignTaxYear) return;
    this.assignLoading = true;
    const url = `${this.adminApiUrl}/getScenariosJCDs?taxYear=${this.assignTaxYear}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.assignLoading = false;
        this.assignJcds = Array.isArray(res?.jcds) ? res.jcds : [];
        this.assignScenarios = Array.isArray(res?.scenarios) ? res.scenarios : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.assignLoading = false;
        this.assignJcds = [];
        this.assignScenarios = [];
        this.cdr.detectChanges();
      },
    });
  }

  private loadAssignHierarchiesAndDefault(): void {
    if (!this.assignTaxYear || !this.assignScenario || !this.assignJcdKey) return;
    this.assignLoading = true;
    const app = this.assignJcds.find((j: any) => Number(j?.jcd_key) === Number(this.assignJcdKey));
    const appCode = app?.id ?? app?.channel_key;
    const channelKey = app?.channel_key;
    if (!appCode || !channelKey) {
      this.assignLoading = false;
      return;
    }
    const defaultUrl =
      `${this.adminApiUrl}/hierarchy/getDefault?appCode=${channelKey}` +
      `&taxYear=${this.assignTaxYear}&scenarioId=${this.assignScenario}&jcdKey=${this.assignJcdKey}`;
    const listUrl =
      `${this.adminApiUrl}/getHierarchiesForApp?taxYear=${this.assignTaxYear}` +
      `&scenarioId=${this.assignScenario}&jcdKey=${this.assignJcdKey}&appCode=${appCode}`;

    this.http.get<any>(defaultUrl, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (defaultRes) => {
        this.http.post<any>(listUrl, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
          next: (listRes) => {
            this.assignLoading = false;
            this.assignHierarchies = Array.isArray(listRes) ? listRes : [];
            const defaultId = Number(defaultRes?.hierarchyId ?? 0);
            const defaultHierarchy = this.assignHierarchies.find((h: any) => Number(h?.hierarchy_id) === defaultId);
            this.assignDefaultHierarchyName = defaultHierarchy?.hierarchy_name ?? 'None';
            this.cdr.detectChanges();
          },
          error: () => {
            this.assignLoading = false;
            this.assignHierarchies = [];
            this.assignDefaultHierarchyName = 'None';
            this.cdr.detectChanges();
          },
        });
      },
      error: () => {
        this.assignLoading = false;
        this.assignHierarchies = [];
        this.assignDefaultHierarchyName = 'None';
        this.cdr.detectChanges();
      },
    });
  }

  private loadCreateDestinationHierarchies(): void {
    if (!this.createDestAppCode || !this.createDestJcdKey) {
      this.createDestHierarchies = [];
      return;
    }
    const url =
      `${this.adminApiUrl}/getHierarchiesForApp?taxYear=${this.currentTaxYear}` +
      `&scenarioId=${this.currentScenario}&jcdKey=${this.createDestJcdKey}&appCode=${this.createDestAppCode}`;
    this.http.post<any>(url, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.createDestHierarchies = Array.isArray(res) ? res : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.createDestHierarchies = [];
        this.cdr.detectChanges();
      },
    });
  }

  private loadExcelDestinationHierarchies(): void {
    if (!this.excelDestAppCode || !this.excelDestJcdKey) {
      this.excelDestHierarchies = [];
      return;
    }
    const url =
      `${this.adminApiUrl}/getHierarchiesForApp?taxYear=${this.currentTaxYear}` +
      `&scenarioId=${this.currentScenario}&jcdKey=${this.excelDestJcdKey}&appCode=${this.excelDestAppCode}`;
    this.http.post<any>(url, {}, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.excelDestHierarchies = Array.isArray(res) ? res : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.excelDestHierarchies = [];
        this.cdr.detectChanges();
      },
    });
  }

  private findAppById(list: any[], id: string): any {
    return (list || []).find((a: any) => String(a?.id) === String(id));
  }

  private toNumberOrNull(v: any): number | null {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  get excelHierarchyNameExists(): boolean {
    const name = String(this.excelHierarchyName || '').trim().toLowerCase();
    if (!name) return false;
    return (this.excelDestHierarchies || []).some(
      (h: any) => String(h?.hierarchy_name || '').trim().toLowerCase() === name
    );
  }

  get createHierarchyNameExistsInDestination(): boolean {
    const name = String(this.createHierarchyName || '').trim().toLowerCase();
    if (!name) return false;
    return (this.createDestHierarchies || []).some(
      (h: any) => String(h?.hierarchy_name || '').trim().toLowerCase() === name
    );
  }

  get createHierarchyNameExistsInSource(): boolean {
    const name = String(this.createHierarchyName || '').trim().toLowerCase();
    if (!name || String(this.leftApp || '') !== String(this.createDestAppCode || '')) return false;
    return (this.hierarchiesLeft || []).some(
      (h: any) => String(h?.hierarchy_name || '').trim().toLowerCase() === name
    );
  }

  private findNodeInTreeByMeCode(nodes: any[], meCode: string): any {
    for (const n of nodes || []) {
      if (String(n?.meCode) === String(meCode)) return n;
      const child = this.findNodeInTreeByMeCode(n?.children || [], meCode);
      if (child) return child;
    }
    return null;
  }

  private findNodeInTreeByMeKey(nodes: any[], meKey: any): any {
    for (const n of nodes || []) {
      if (String(n?.meKey) === String(meKey)) return n;
      const child = this.findNodeInTreeByMeKey(n?.children || [], meKey);
      if (child) return child;
    }
    return null;
  }

  private containsNodeByMeKey(root: any, meKey: any): boolean {
    if (!root) return false;
    if (String(root?.meKey) === String(meKey)) return true;
    const children = Array.isArray(root?.children) ? root.children : [];
    for (const child of children) {
      if (this.containsNodeByMeKey(child, meKey)) return true;
    }
    return false;
  }

  canDragDropOnNode(node: any): boolean {
    if (!this.dragMovingNode || !node) return false;
    if (String(node?.meKey) === String(this.dragMovingNode?.meKey)) return false;
    return !this.containsNodeByMeKey(this.dragMovingNode, node?.meKey);
  }

  isDragOverNode(node: any): boolean {
    return !!node && String(this.dragOverNodeKey ?? '') === String(node?.meKey ?? '');
  }

  private executeMoveHierarchy(srcNode: any, destNode: any): void {
    if (!srcNode || !this.rightHierarchyId || !this.rightApp) return;
    const app = this.findAppById(this.appsRight, this.rightApp);
    if (!app?.jcd_key) return;
    const targetParentKey = destNode?.meKey ?? '';
    const targetHierarchyId = destNode?.hierarchyId ?? this.rightHierarchyId;

    // Edge case: no-op move (already under same parent)
    if (String(srcNode?.meParentKey ?? '') === String(targetParentKey)) {
      this.dragMovingNode = null;
      this.dragOverNodeKey = null;
      this.dragOverRoot = false;
      this.gtwSnackbarService.openSnackBar('Node is already under selected parent', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    // Edge case: avoid cycle by dropping into own descendant
    if (destNode && this.containsNodeByMeKey(srcNode, destNode?.meKey)) {
      this.gtwSnackbarService.openSnackBar('Invalid move: cannot move node into its own descendant', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    // Edge case: duplicate meCode under same target parent
    if (this.existsSiblingWithSameCode(srcNode, targetParentKey, destNode)) {
      this.gtwSnackbarService.openSnackBar('Target parent already contains a child with same BU code', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    const payload = {
      meKey: srcNode.meKey,
      meParentKey: targetParentKey,
      srcHierarchyId: srcNode.hierarchyId ?? this.rightHierarchyId,
      descHierarchyId: targetHierarchyId,
      tax_year: this.currentTaxYear,
      scenario: this.currentScenario,
      jcd_key: app.jcd_key,
    };
    this.treeLoading = true;
    this.http.post<any>(`${this.adminApiUrl}/moveBusinessHierarchy`, payload, { withCredentials: true }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.treeLoading = false;
        this.dragMovingNode = null;
        this.dragOverNodeKey = null;
        this.dragOverRoot = false;
        this.gtwSnackbarService.openSnackBar('Moved Business Unit Successfully', 'gtw-snackbar--green');
        this.loadTree('right');
        this.cdr.detectChanges();
      },
      error: () => {
        this.treeLoading = false;
        this.gtwSnackbarService.openSnackBar('Move Business Unit failed', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  private existsSiblingWithSameCode(srcNode: any, targetParentKey: string, destNode: any): boolean {
    const sourceCode = String(srcNode?.meCode ?? '');
    if (!sourceCode) return false;
    if (!targetParentKey) {
      // root-level move: check top-level right tree
      return (this.rightNodes || []).some((n: any) => String(n?.meCode ?? '') === sourceCode && String(n?.meKey ?? '') !== String(srcNode?.meKey ?? ''));
    }
    const targetParent = destNode || this.findNodeInTreeByMeKey(this.rightNodes, targetParentKey);
    const children = Array.isArray(targetParent?.children) ? targetParent.children : [];
    return children.some((n: any) => String(n?.meCode ?? '') === sourceCode && String(n?.meKey ?? '') !== String(srcNode?.meKey ?? ''));
  }
}
